import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';
import { History, Settings } from 'lucide-react';
import { useExposure } from '../../../../../hooks/useExposure';

const TabContainer = styled.div`
  display: flex;
  gap: 4px;
  background: #292d2e;
  height: 48px;
  padding: 0 16px;
  align-items: center;
  border-bottom: 1px solid rgb(50, 55, 56);
`;

const TabButton = styled.button`
  padding: 12px 20px;
  background: ${props => props.active ? 'rgb(50, 55, 56)' : 'transparent'};
  color: ${props => props.active ? 'rgb(240, 156, 6)' : '#cccccc'};
  border: none;
  cursor: pointer;
  font-weight: bold;
  font-size: 14px;
  font-family: 'Montserrat Alternates', sans-serif;
  transition: all 0.3s ease;
  border-radius: 8px 8px 0 0;
  position: relative;
  min-width: 80px;

  &:hover {
    background-color: ${props => props.active ? 'rgb(60, 65, 66)' : 'rgb(50, 55, 56)'};
    color: ${props => props.active ? 'rgb(240, 156, 6)' : '#ffffff'};
  }

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background-color: ${props => props.active ? 'rgb(240, 156, 6)' : 'transparent'};
    border-radius: 8px 8px 0 0;
    transition: all 0.3s ease;
  }
`;

const HistoryButton = styled.button`
  background: transparent;
  border: none;
  color: #cccccc;
  cursor: pointer;
  padding: 8px;
  margin-left: auto;
  display: flex;
  align-items: center;
  transition: color 0.2s ease;
  font-family: 'Montserrat Alternates', sans-serif;

  &:hover {
    color: rgb(240, 156, 6);
  }
`;

const SettingsWrapper = styled.div`
  margin-left: 8px;
  position: relative;
`;

const SettingsButton = styled.button`
  background: transparent;
  border: none;
  color: #cccccc;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  transition: color 0.2s ease;

  &:hover {
    color: rgb(240, 156, 6);
  }
`;

const ExposureModal = styled.div`
  position: absolute;
  top: 110%;
  right: 0;
  background: #1e1e1e;
  border: 1px solid rgb(50, 55, 56);
  border-radius: 8px;
  padding: 12px 16px;
  min-width: 200px;
  color: white;
  font-size: 14px;
  font-family: 'Montserrat Alternates', sans-serif;
  box-shadow: 0px 4px 10px rgba(0,0,0,0.6);
  z-index: 999;
`;

const ExposureRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 6px;
  width:auto;

  &:last-child {
    margin-bottom: 0;
  }
`;

const TabSwitcher = ({ activeTab, onTabClick }) => {
  const history = useHistory();
  const { exposure, totalExposure } = useExposure();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  const handleHistoryClick = () => {
    history.push('/sports/markets/history');
  };

  // Close when clicked outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <TabContainer>
      <TabButton 
        active={activeTab === 'in-play'}
        onClick={() => onTabClick('in-play')}
      >
        In-Play
      </TabButton>
      <TabButton
        active={activeTab === 'today'}
        onClick={() => onTabClick('today')}
      >
        Today
      </TabButton>
      <TabButton
        active={activeTab === 'tomorrow'}
        onClick={() => onTabClick('tomorrow')}
      >
        Tomorrow
      </TabButton>

      {/* Settings icon replaces ExposureText */}
     

      <HistoryButton onClick={handleHistoryClick}>
        <History size={20} />
      </HistoryButton>
       <SettingsWrapper ref={wrapperRef}>
        <SettingsButton onClick={() => setOpen(prev => !prev)}>
          <Settings size={20} />
        </SettingsButton>

        {open && (
          <ExposureModal>
            <ExposureRow>
              <span>Total Exposure:</span>
              <strong style={{ color: "rgb(240, 156, 6)" }}>
                {totalExposure}
              </strong>
            </ExposureRow>
            {Object.entries(exposure || {}).map(([market, value]) => (
              <ExposureRow key={market}>
                <span>{market}</span>
                <span>{value}</span>
              </ExposureRow>
            ))}
          </ExposureModal>
        )}
      </SettingsWrapper>
    </TabContainer>
  );
};

export default TabSwitcher;
